import type { Kind } from "./types";

export const NAMESPACE = "mymods";

export const BASE_ITEMS: Record<Exclude<Kind, "mob">, readonly string[]> = {
  weapon: [
    "wooden_sword", "stone_sword", "iron_sword", "golden_sword", "diamond_sword", "netherite_sword",
    "iron_axe", "diamond_axe", "netherite_axe", "trident", "mace", "stick", "blaze_rod", "bone",
  ],
  tool: [
    "iron_pickaxe", "diamond_pickaxe", "netherite_pickaxe", "iron_axe", "diamond_axe", "netherite_axe",
    "iron_shovel", "diamond_shovel", "netherite_shovel", "iron_hoe", "diamond_hoe", "netherite_hoe",
  ],
  item: [
    "stick", "blaze_rod", "breeze_rod", "bone", "amethyst_shard", "echo_shard", "nether_star",
    "heart_of_the_sea", "ender_eye", "feather", "totem_of_undying", "book", "compass", "clock",
  ],
  food: [
    "apple", "golden_apple", "bread", "cookie", "cake", "pumpkin_pie", "baked_potato", "cooked_beef",
    "cooked_chicken", "melon_slice", "sweet_berries", "glow_berries", "honey_bottle", "carrot", "golden_carrot",
  ],
  armor: [
    "iron_helmet", "iron_chestplate", "iron_leggings", "iron_boots",
    "diamond_helmet", "diamond_chestplate", "diamond_leggings", "diamond_boots",
    "netherite_helmet", "netherite_chestplate", "netherite_leggings", "netherite_boots",
    "golden_helmet", "golden_chestplate", "golden_leggings", "golden_boots",
    "leather_helmet", "leather_chestplate", "leather_leggings", "leather_boots", "turtle_helmet",
  ],
};

export const DEFAULT_BASE_ITEM: Record<Exclude<Kind, "mob">, string> = {
  weapon: "diamond_sword",
  tool: "diamond_pickaxe",
  item: "blaze_rod",
  food: "golden_apple",
  armor: "diamond_chestplate",
};

export const MOBS: readonly string[] = [
  "zombie", "husk", "skeleton", "stray", "creeper", "spider", "cave_spider", "enderman", "blaze",
  "wither_skeleton", "piglin", "piglin_brute", "vindicator", "pillager", "evoker", "ravager", "slime",
  "magma_cube", "ghast", "phantom", "breeze", "bogged", "warden", "iron_golem", "snow_golem", "wolf",
  "cat", "fox", "pig", "cow", "sheep", "chicken", "horse", "llama", "panda", "polar_bear", "axolotl",
  "frog", "bee", "parrot", "rabbit", "turtle", "dolphin", "camel", "sniffer", "armadillo", "allay",
  "villager", "goat", "strider", "hoglin", "zoglin", "guardian", "witch", "drowned",
];

export const ALLY_MOBS: readonly string[] = ["wolf", "iron_golem", "snow_golem"];

export const EFFECTS: readonly string[] = [
  "speed", "slowness", "haste", "mining_fatigue", "strength", "instant_health", "instant_damage",
  "jump_boost", "nausea", "regeneration", "resistance", "fire_resistance", "water_breathing",
  "invisibility", "blindness", "night_vision", "hunger", "weakness", "poison", "wither",
  "health_boost", "absorption", "saturation", "glowing", "levitation", "luck", "slow_falling",
  "conduit_power", "dolphins_grace", "darkness", "wind_charged", "weaving", "oozing", "infested",
];

export const MAINHAND_ITEMS: readonly string[] = [
  ...BASE_ITEMS.weapon, "bow", "crossbow", "shield", "torch", "golden_axe", "stone_axe",
];

export const INGREDIENTS: readonly string[] = [
  "stick", "string", "feather", "flint", "leather", "paper", "book", "bone", "bone_meal",
  "coal", "charcoal", "iron_ingot", "iron_nugget", "gold_ingot", "gold_nugget", "copper_ingot",
  "diamond", "emerald", "netherite_ingot", "netherite_scrap", "lapis_lazuli", "redstone",
  "quartz", "amethyst_shard", "glowstone_dust", "gunpowder", "blaze_rod", "blaze_powder",
  "ender_pearl", "ender_eye", "ghast_tear", "magma_cream", "slime_ball", "phantom_membrane",
  "nether_star", "echo_shard", "heart_of_the_sea", "nautilus_shell", "prismarine_shard",
  "prismarine_crystals", "breeze_rod", "wind_charge", "heavy_core", "dragon_breath", "honeycomb",
  "apple", "golden_apple", "carrot", "golden_carrot", "sugar", "wheat", "egg", "milk_bucket",
  "cocoa_beans", "sweet_berries", "glow_berries", "melon_slice", "pumpkin", "honey_bottle",
  "cobblestone", "stone", "obsidian", "crying_obsidian", "glass", "sand", "gravel", "dirt",
  "oak_planks", "oak_log", "torch", "tnt", "lightning_rod", "fire_charge", "snowball", "ice",
  "packed_ice", "blue_ice", "magma_block", "netherrack", "soul_sand", "end_stone", "chorus_fruit",
  "shulker_shell", "rabbit_foot", "spider_eye", "rotten_flesh", "turtle_scute", "armadillo_scute",
  "copper_block", "iron_block", "gold_block", "diamond_block", "emerald_block", "redstone_block",
  "lapis_block", "amethyst_block", "white_wool", "red_dye", "blue_dye", "green_dye", "yellow_dye",
  "black_dye", "purple_dye",
];

export const ABILITY_TYPES = [
  "buff", "heal", "leap", "blink", "lightning", "meteor", "shockwave", "summon_allies",
] as const;

export const HIT_POWER_TYPES = ["effect", "self_effect", "ignite", "lightning", "extra_damage"] as const;

export const LIMITS = {
  nameLength: 40,
  descriptionLength: 120,
  texturePromptLength: 200,
  idLength: 32,
  hitPowers: 3,
  effects: 3,
  effectSeconds: [1, 60],
  effectLevel: [1, 5],
  attackDamage: [1, 40],
  attackSpeed: [0.5, 4],
  armor: [0, 20],
  armorToughness: [0, 12],
  moveSpeed: [-0.5, 1],
  maxHealth: [0, 40],
  durability: [1, 5000],
  cooldownSeconds: [0.5, 60],
  igniteSeconds: [1, 15],
  extraDamage: [1, 20],
  abilityPower: [1, 5],
  meteorPower: [1, 3],
  distance: [4, 24],
  radius: [2, 10],
  shockDamage: [1, 20],
  allyCount: [1, 3],
  nutrition: [1, 20],
  saturation: [0, 20],
  mobHealth: [1, 500],
  mobScale: [0.25, 5],
  mobDamage: [0, 40],
  mobSpeedLevel: [-3, 3],
} as const;

export const ARMOR_SLOT_BY_SUFFIX: Record<string, string> = {
  helmet: "head",
  chestplate: "chest",
  leggings: "legs",
  boots: "feet",
};
